// Set merges — products the catalog splits into several set ids but that are
// one product on the shelf (BUG-010).
//
// pokemontcg.io lists 30th Celebration as two sets: the 161-card main set
// (me55) and its 30-card Classic Collection subset (me55c). They come out of
// the same packs, so the app shows them as ONE set of 191 cards: the child
// set is hidden from every set list, its cards load after the parent's under
// their own section header, and completion / hit rate count them toward the
// parent.
//
// Card ids are never rewritten (me55c-4 stays me55c-4), so saved collection,
// wishlist and trade rows keep working untouched. Membership is always
// decided by set id, never by card number — Classic Collection cards keep
// their original-set numbers, and three of them share #106 (me55c-106,
// me55c-106p, me55c-106m).

// child set id -> { parent, label }. `label` is the section header shown
// above the child's cards inside the parent set.
export const SET_MERGES = {
  me55c: { parent: 'me55', label: 'Classic Collection' },
};

// Card ids are `{setId}-{number}` and setIds can contain hyphens — always
// split on the LAST hyphen (same rule as cardIndex.setIdFromCardId).
const setIdOfCardId = (cardId) => cardId.slice(0, cardId.lastIndexOf('-'));

export const isMergedChildSet = (setId) => Boolean(SET_MERGES[setId]);

// The set a set id is displayed under: the parent for a merged child,
// otherwise itself.
export const getParentSetId = (setId) => SET_MERGES[setId]?.parent || setId;

// Child set ids merged into `parentId`, in declaration order.
export const getMergedChildIds = (parentId) =>
  Object.keys(SET_MERGES).filter((childId) => SET_MERGES[childId].parent === parentId);

// Every set id whose cards belong to `setId` as displayed: itself plus any
// merged children.
export const getMergedSetIds = (setId) => [setId, ...getMergedChildIds(setId)];

export const getMergedSectionLabel = (setId) => SET_MERGES[setId]?.label || null;

// Does this collection card id count toward the displayed set `setId`?
// Prefix match per member set (not parent-of-card lookup) so plain sets keep
// the exact semantics they had before merges existed.
export const cardIdBelongsToSet = (cardId, setId) =>
  getMergedSetIds(setId).some((id) => cardId.startsWith(`${id}-`));

// Collapse a raw sets list: hide merged children whose parent is present and
// fold their card count into the parent's `total`. `printedTotal` is left
// alone — it's the denominator printed on the parent's own cards.
export const mergeSetList = (sets) => {
  const byId = new Map(sets.map((s) => [s.id, s]));
  return sets
    .filter((s) => !(isMergedChildSet(s.id) && byId.has(getParentSetId(s.id))))
    .map((s) => {
      const children = getMergedChildIds(s.id).map((id) => byId.get(id)).filter(Boolean);
      if (children.length === 0) return s;
      return {
        ...s,
        total: (s.total || 0) + children.reduce((sum, c) => sum + (c.total || 0), 0),
        mergedSetIds: [s.id, ...children.map((c) => c.id)],
      };
    });
};

// Display sort for a (possibly merged) set's cards: the parent's own cards
// first, then each merged child in declaration order; within a set, numeric
// numbers ascending, non-numeric ("SV01") after, compared lexically. Card id
// breaks ties so duplicate numbers (me55c-106 / -106p / -106m) order
// deterministically.
export const sortSetCards = (cards) => {
  const rank = (card) => {
    const setId = card.set?.id || setIdOfCardId(card.id);
    if (!isMergedChildSet(setId)) return 0;
    return 1 + Object.keys(SET_MERGES).indexOf(setId);
  };
  return [...cards].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    const numA = parseInt(a.number, 10);
    const numB = parseInt(b.number, 10);
    if (isNaN(numA) && isNaN(numB)) {
      const byNumber = String(a.number).localeCompare(String(b.number));
      if (byNumber !== 0) return byNumber;
    } else if (isNaN(numA)) {
      return 1;
    } else if (isNaN(numB)) {
      return -1;
    } else if (numA !== numB) {
      return numA - numB;
    }
    return String(a.id).localeCompare(String(b.id));
  });
};

// Card number as shown on a tile/modal. Merged-child cards carry their
// ORIGINAL set's number, so "4/30" (number over the subset's size) would be a
// made-up fraction — show the bare "#4" instead.
export const formatCardNumber = (card) => {
  if (isMergedChildSet(card.set?.id)) return `#${card.number}`;
  const printedTotal = card.set?.printedTotal || card.set?.total;
  return printedTotal ? `${card.number}/${printedTotal}` : `#${card.number}`;
};
